// ============================================================================
// File: frontend/src/components/dashboard/reputation/SmartReputationFeed.js
// Version: 3.0
// Date: 2025-12-31 16:30:00 EST
// Author: Alexander Fitenko
// Description: Smart scrollable feed of recent AI Battle events with Battle Cards,
//              Competitor Extraction, Contextual Actions, and Fixed Layout.
//              Implements filter logic for All Actions, Wins, Losses, In Progress, Trends.
// 
// Changelog:
// v3.0 (2025-12-31) - 🔧 CRITICAL FIX: Filter Logic
//                   - Fixed: Changed b.ai_results to b.results (backend compatibility)
//                   - Fixed: Wins filter now correctly reads rank data from results array
//                   - Fixed: Losses filter now shows battles with rank > 10 or null
//                   - Fixed: Trends filter correctly sorts by _bestRank
//                   - Fixed: All filters now work with correct data structure
//                   - Root cause: Backend returns 'results' not 'ai_results'
// v2.3 (Previous)   - Battle Cards & Layout Fixes
// 
// DEPLOYMENT:
// Copy this file to: frontend/src/components/dashboard/reputation/SmartReputationFeed.js
// Then restart frontend: cd frontend && pnpm start
// ============================================================================

import React, { useState, useMemo } from 'react';
import { Star, Shield, TrendingUp, AlertTriangle, Crosshair, ChevronRight, X, Loader2, CheckCircle2, XCircle, ArrowRight, Activity, FileText, Code2, MapPin, Trophy, Eye, CheckCircle, ShieldAlert, Building, Zap, Search } from 'lucide-react';
import moment from 'moment';
import BattleCard from './BattleCard';
import ContentWarfareModal from '../../reputation/ContentWarfareModal'; // [NEW] Unified Command Center
import apiService from '../../../services/apiService';

const formatRankDisplay = (rank) => {
    if (rank === null || rank === undefined) return "—";
    if (rank >= 999) return "Not in Top 10";
    return `#${rank}`;
};

const SmartReputationFeed = ({ battles, height, onFixRequired, onInspect, onRefreshData, defaultLocation }) => {
    // State
    const [visibleCount, setVisibleCount] = useState(50);
    const [filter, setFilter] = useState('all'); // all, win, loss, recovered
    const [fixingIds, setFixingIds] = useState({});
    const [viewDetailsBattle, setViewDetailsBattle] = useState(null);
    const [modalMode, setModalMode] = useState('attack'); // [NEW] 'view' (history) or 'attack' (resolve)

    // Reset pagination on filter change
    React.useEffect(() => {
        setVisibleCount(50);
    }, [filter]);

    // Audit State
    const [auditStatus, setAuditStatus] = useState('idle'); // idle, loading, complete
    const [auditData, setAuditData] = useState(null); // { gap: 'schema', summary: '...', instructions: '...' }
    const [auditLogs, setAuditLogs] = useState([]); // For real-time log display
    const [auditStage, setAuditStage] = useState(0); // 0: idle, 1: init, 2: scanning, 3: analyzing, 4: complete

    // [NEW] Polling for Live Tasks with Safety Checks
    const [activeTasks, setActiveTasks] = useState([]);

    const fetchTasks = React.useCallback(async () => {
        try {
            const data = await apiService.makeRequest('/reputation/tasks');
            // Only update if data actually changed (Deep compare or length check to reduce renders)
            setActiveTasks(prev => {
                if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                return data;
            });
        } catch (error) {
            console.error("Task Polling Error:", error);
        }
    }, []);

    React.useEffect(() => {
        let isMounted = true;
        fetchTasks(); // Initial Load

        // Polling Interval (Reduced frequency to 10s to be safe)
        const interval = setInterval(fetchTasks, 10000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [fetchTasks]); // fetchTasks is stable via useCallback

    // [NEW] Handlers
    // [NEW] Handlers
    const handleView = (battle) => {
        // [FIX] Force Unified Modal (Content Warfare)
        // Ensure we open the "Battle Report" tab
        setModalMode('report');
        setViewDetailsBattle(battle);
    };

    const handleResolve = (battle) => {
        // [FIX] Force Unified Modal (Content Warfare)
        // Ensure we open the "Attack Plan" tab
        setModalMode('attack');
        setViewDetailsBattle(battle);
    };

    const handleStartAudit = async (battle) => {
        setAuditStatus('loading');
        setAuditLogs(["Initializing Tactical Audit Protocol..."]);
        setAuditStage(1);

        // Simulate Progress Stages (Visual Only)
        // We use a ref or simple variable if we could, but interval is fine for now
        // Ideally we should clear this interval in useEffect or finally block
        let intervalId = null;

        intervalId = setInterval(() => {
            setAuditLogs(prev => {
                const logs = [...prev];
                if (logs.length > 8) logs.shift(); // Keep only recent

                // Random tactical logs based on stage
                const tacticalMsgs = [
                    "Scanning target site structure...",
                    "Extracting semantic core entities...",
                    "Validating competitor Schema.org alignment...",
                    "Checking local citation consistency...",
                    "Analyzing H1-H3 hierarchy gaps...",
                    "Detecting missing service area polygons..."
                ];
                return [...logs, tacticalMsgs[Math.floor(Math.random() * tacticalMsgs.length)]];
            });
        }, 1200);

        try {
            const data = await apiService.makeRequest('/reputation/audit-gap', {
                method: 'POST',
                body: JSON.stringify({
                    query: battle.keyword,
                    location: battle.location,
                    competitor_names: [
                        "Competitor 1", "Competitor 2"
                    ],
                    client_profile_id: battle.profile_id
                })
            });

            clearInterval(intervalId);
            setAuditData(data);
            setAuditStatus('complete');
            setAuditStage(4);
            setAuditLogs(prev => [...prev, "Audit Completed Successfully. Strategy Generated."]);

        } catch (error) {
            if (intervalId) clearInterval(intervalId);
            console.error(error);
            setAuditStatus('idle');
            alert("Audit failed. Please try again.");
        }
    }


    const handleQueueTask = async (targetBattle, tactic, providedRecipe) => {
        const battleToUse = targetBattle || detailsBattle;
        // [FIX] Accept recipe from Modal or fall back to local state
        const recipeToUse = providedRecipe || (auditData && auditData.recipe) || { mode: "nuclear", auto_generated: true };

        if (!battleToUse) return;

        try {
            // [FIX] Force HTTPS and use simplified /tasks endpoint (Matches Backend)
            await apiService.makeRequest('/reputation/tasks', {
                method: 'POST',
                body: JSON.stringify({
                    source_battle_id: battleToUse.id,
                    location_id: battleToUse.location, // Using location string for now
                    query: battleToUse.keyword,
                    priority: "high",
                    recipe: recipeToUse,
                    ai_config: { mode: tactic || "nuclear" },
                    client_profile_id: battleToUse.profile_id
                })
            });

            alert("Strategic Task Queued! 'Nuclear Mode' execution started.");

            // [FIX] Immediate Optimistic Update to prevent "vanishing"
            setFixingIds(prev => ({ ...prev, [detailsBattle.id]: true }));

            setViewDetailsBattle(null);
            if (onFixRequired) onFixRequired(detailsBattle.id, detailsBattle.location, detailsBattle.keyword); // Notify parent to switch tab if needed
            if (onRefreshData) onRefreshData(); // [NEW] Force Refresh

        } catch (error) {
            console.error(error);
            alert("Error queuing task. Please try again.");
        }
    };


    // [NEW] Shared Helper for text parsing (Matches BattleCard.js)
    const getOwnRanks = (txt) => {
        if (!txt) return [];
        // [FIX] Robust Regex compatible with Python backend
        // Matches "1. Morgan Legal", "2) Morgan Legal", "**4.** Morgan Legal"
        // Also supports numbered bullet points
        const regex = /(\d+)[\.\)]\s+(?:\*\*|__)?.*Morgan Legal/gi;
        const matches = [...txt.matchAll(regex)];
        return matches.map(m => parseInt(m[1], 10)).sort((a, b) => a - b);
    };

    const getTextToParse = (battle) => {
        let raw = battle.initial_message || battle.message_content;
        try {
            if (typeof raw === 'string' && (raw.startsWith('{') || raw.startsWith('['))) {
                const parsed = JSON.parse(raw);
                return parsed.ai_response || parsed.result || parsed.content || JSON.stringify(parsed);
            }
        } catch (e) { }
        return raw || "";
    };

    // Filter Logic
    const filteredBattles = useMemo(() => {
        if (!battles) return [];

        // Debug Console (Temporary)
        console.log('Battle Statuses:', battles.map(b => ({ id: b.id, status: b.active_task_status, battleStatus: b.status })));

        // 1. Pre-calculate Status strictly based on FRONTEND parsing (Consistency)
        const annotated = battles.map(b => {
            const txt = getTextToParse(b);

            // [FIX v2] Calculate BEST rank from ALL sources (Google + AI)
            // Priority: Best AI Rank > Google Rank > Parsed Rank
            let bestRank = null;

            // 1. Check AI ranks from ATTACK phase (Gemini, Grok, DeepSeek)
            const aiRanks = [];
            if (b.results && Array.isArray(b.results)) {
                b.results.forEach(ai => {
                    // Only consider ATTACK phase results
                    if (ai.round_phase === 'attack' && ai.rank_position && ai.rank_position < 999) {
                        aiRanks.push(ai.rank_position);
                    }
                });
            }

            // 2. Get Google Organic rank
            const googleResult = (b.results || []).find(r => r.engine === 'google_organic' && r.round_phase === 'audit');
            const googleRank = b.google_rank || b.rank || (googleResult ? googleResult.rank_position : null);

            // 3. Fallback to local parsing if needed
            let parsedRank = null;
            if (!googleRank && aiRanks.length === 0) {
                const ownRanks = getOwnRanks(txt);
                parsedRank = ownRanks.length > 0 ? ownRanks[0] : null;
            }

            // 4. Determine BEST rank (lowest number wins)
            const allRanks = [
                ...aiRanks,
                googleRank,
                parsedRank
            ].filter(r => r !== null && r !== undefined && r < 999);

            bestRank = allRanks.length > 0 ? Math.min(...allRanks) : null;

            // const isMentioned = /Morgan Legal|MLG/i.test(txt); // [REMOVED] Causing false positives in Wins filter

            let status = 'loss';
            if (bestRank && bestRank <= 3) status = 'win'; // Elite Win: Top 3
            else if (bestRank && bestRank <= 10) status = 'visible'; // Visible: 4-10 (First Page)

            // [NEW] Persisted or Local "In Progress" state
            // If backend says active task OR we just clicked fix locally
            // [CRITICAL UPDATE] Expanded Status Dictionary: Ensure ALL backend statuses related to active work are caught.
            const ACTIVE_TASK_VALUES = [
                // Old statuses
                'queued', 'processing', 'running', 'pending', 'fixing_in_progress',
                // AI Content Warfare statuses
                'generating', 'optimizing', 'writing', 'publishing',
                // Auto-Defense
                'queued_auto', 'auto_queued',
                // Intermediate
                'queued_pending', 'processing_ai',
                // Fallback
                'active'
            ];

            const COMPLETED_VALUES = ['completed', 'success', 'failed', 'cancelled', 'done'];

            const taskStatus = (b.active_task_status || '').toLowerCase();
            const battleStatus = (b.status || '').toLowerCase();

            // Check if backend reports active
            const isBackendActive = ACTIVE_TASK_VALUES.includes(taskStatus) || ACTIVE_TASK_VALUES.includes(battleStatus);

            // Optimistic UI
            const isOptimisticActive = fixingIds[b.id] === true;

            // [FIX] Priority Logic:
            // 1. If we hold an Optimistic Lock (fixingIds), it IS in progress.
            // 2. If Backend says it's active (isBackendActive), it IS in progress.
            // 3. [NEW] If Task says it's active for this Battle ID (isLinkedActive), it IS in progress.

            // Cross-Reference with Polled Active Tasks
            // Matches if any active task lists this battle as 'source_battle_id'
            const isLinkedActive = activeTasks.some(t =>
                t.source_battle_id === b.id &&
                ['queued', 'queued_pending', 'processing', 'processing_ai', 'running', 'generating', 'published_monitoring'].includes(t.status?.toLowerCase())
            );

            // We REMOVED the check for (!isBackendCompleted) because it was hiding active tasks on completed battles.
            const isInProgress = isOptimisticActive || isBackendActive || isLinkedActive;

            // [FIX] Embed computed values into object for filter usage
            return { ...b, _uiStatus: status, _bestRank: bestRank, _isInProgress: isInProgress };
        });

        // 2. Strict Filtering & Bucketing
        // [FIX] Removed hardcoded .slice() to support Infinite Scroll / Load More logic
        if (filter === 'all') {
            return annotated;
        }
        if (filter === 'progress') {
            // [NEW] Show only fixing items
            return annotated.filter(b => b._isInProgress);
        }
        if (filter === 'win') {
            // [FIX v3] Individual Thresholds for Each Engine
            return annotated.filter(b => {
                // Define individual rank thresholds for each source
                const THRESHOLDS = {
                    'google': 15,      // Google Organic: Top-15
                    'gpt4': 4,         // GPT-4: Top-4
                    'gemini': 9,       // Gemini: Top-9
                    'deepseek': 10,    // DeepSeek: Top-10
                    'deepseek_r1': 10, // DeepSeek R1: Top-10
                    'grok': 3          // Grok: Top-3
                };

                // Get ranks for each source
                const googleResult = (b.results || []).find(r => r.engine === 'google_organic' && r.round_phase === 'audit');

                const ranks = {
                    'google': b.google_rank || b.rank || (googleResult ? googleResult.rank_position : null),
                    'gpt4': null,
                    'gemini': null,
                    'deepseek': null,
                    'grok': null
                };

                // Extract AI ranks from ai_results (ATTACK phase only)
                if (b.results && Array.isArray(b.results)) {
                    b.results.forEach(ai => {
                        if (ai.round_phase === 'attack' && ai.rank_position && ai.rank_position < 999) {
                            const engine = ai.engine.toLowerCase();
                            if (engine === 'deepseek_r1') {
                                ranks['deepseek'] = ai.rank_position;
                            } else if (engine in ranks) {
                                ranks[engine] = ai.rank_position;
                            }
                        }
                    });
                }

                // Check if ALL sources meet their individual thresholds
                // (or are missing/error - we ignore missing sources)
                let allPass = true;
                let hasAtLeastOne = false;

                for (const [engine, threshold] of Object.entries(THRESHOLDS)) {
                    const rank = ranks[engine];

                    // If source has a rank, check if it meets threshold
                    if (rank !== null && rank !== undefined) {
                        hasAtLeastOne = true;
                        if (rank > threshold) {
                            allPass = false;
                            break;
                        }
                    }
                    // If source is missing (null), we ignore it (don't fail)
                }

                // Battle is a WIN if:
                // 1. At least one source returned a rank
                // 2. ALL sources that returned ranks meet their thresholds
                return hasAtLeastOne && allPass;
            });
        }
        if (filter === 'loss') {
            // [FIX v2] Strict Loss bucket: Rank > 10 or Null
            // [FIX] User feedback: Show active losses too! Don't hide In Progress.
            return annotated.filter(b => b._uiStatus === 'loss');
        }
        if (filter === 'trends') {
            // [FIX] "Smart Sorting" - Winners First
            // Tier 1: Win (1-3) ASC
            // Tier 2: Visible (4-10) ASC
            // Tier 3: Losses (11+ or Null) NEWEST FIRST

            const sorted = [...annotated].sort((a, b) => {
                const rankA = a._bestRank !== null ? a._bestRank : 999;
                const rankB = b._bestRank !== null ? b._bestRank : 999;

                // Helper to determine Tier
                const getTier = (r) => {
                    if (r <= 3) return 1;
                    if (r <= 10) return 2;
                    return 3;
                };

                const tierA = getTier(rankA);
                const tierB = getTier(rankB);

                if (tierA !== tierB) {
                    return tierA - tierB; // Lower tier first (1 -> 2 -> 3)
                }

                if (tierA === 1 || tierA === 2) {
                    return rankA - rankB; // Best rank first for winners
                }

                // Tier 3 (Losses) - Newest First
                // Assuming date string ISO
                return new Date(b.created_at || b.date) - new Date(a.created_at || a.date);
            });

            return sorted;
        }
        return annotated;
    }, [battles, filter, fixingIds, activeTasks]);

    // [NEW] Helper for Absolute Date (Fix Future Timestamp)
    const formatBattleDate = (dateString) => {
        if (!dateString) return '';
        // [FIX] Ensure UTC interpretation if 'Z' is missing
        const parseString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
        const date = new Date(parseString);
        const formatted = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York'
        }).format(date);
        return `${formatted} EST`;
    };

    // [NEW] Trends List Item Component
    const TrendItem = ({ item }) => {
        // [FIX] Robust Title Logic
        let rawTitle = item.keyword || item.query || item.title || "Unknown Keyword";
        let displayTitle = rawTitle
            .replace(/\[.*?\]/g, '') // Remove [Persona]
            .replace(/^[\p{Emoji}\s]+/gu, '') // Remove emojis
            .replace(/^Auto:\s*/i, '') // Remove "Auto:" prefix
            .replace(/\(\d{1,2}:\d{2}\)/g, '') // Remove timestamps like (02:17)
            .replace(/\s*\(.*?\)\s*/g, '') // Clean parens metadata
            .trim();

        // Determine Status & Styling
        const isLoss = item._uiStatus === 'loss';
        const isWin = item._uiStatus === 'win';
        const isVisible = item._uiStatus === 'visible';

        // [POLISH] Visual Urgency for Losses
        let borderColor = isLoss ? '#ef4444' : (isWin ? '#10b981' : '#84cc16'); // Red / Emerald / Lime
        let bgColor = isLoss ? '#fff1f2' : (isWin ? '#f0fdf4' : '#f7fee7'); // Rose-50 / Green-50 / Lime-50

        // [FIX] Smart Button Logic
        const isFixing = item._isInProgress; // Already determined in useMemo

        // [FIX] Location Fallback Logic
        const isDateString = (str) => /\d{1,2}:\d{2}/.test(str) || /Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec/i.test(str);
        let displayLocation = item.location;
        if (!displayLocation || displayLocation === 'Unknown Location' || isDateString(displayLocation)) {
            displayLocation = defaultLocation;
        }
        if (!displayLocation) {
            const match = (item.title || "").match(/\(([^)]+)\)/);
            if (match) displayLocation = match[1];
        }
        displayLocation = displayLocation || "Local Market";

        return (
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px',
                background: 'white',
                borderRadius: '8px',
                marginBottom: '8px',
                border: '1px solid #f1f5f9',
                borderLeft: `4px solid ${borderColor}`, // [FIX] Colored Status Indicator
                backgroundColor: isLoss ? '#fff1f2' : 'white', // [POLISH] Background Tint for Losses
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
                <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
                    <div style={{ fontWeight: '700', color: isLoss ? '#be123c' : '#1e293b', fontSize: '0.9rem', marginBottom: '2px' }}>
                        {displayTitle}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: isLoss ? '#be123c' : '#64748b', display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.9 }}>
                        <span>{formatBattleDate(item.created_at || item.date)}</span>
                        <span>•</span>
                        <span style={{ fontWeight: '600' }}>{displayLocation}</span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                        {/* [Use Case] View Report - ALWAYS VISIBLE */}
                        <button
                            onClick={() => handleView(item)}
                            style={{
                                padding: '6px 12px', borderRadius: '6px',
                                border: '1px solid #e2e8f0', background: 'white',
                                color: '#64748b', fontSize: '0.75rem', fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            View Report
                        </button>

                        {/* [Use Case] Action Buttons for Losses */}
                        {isLoss && (
                            <button
                                onClick={() => !isFixing && handleResolve(item)}
                                disabled={isFixing}
                                style={isFixing ? {
                                    // Scenario A: In Progress (Ghost Gray)
                                    padding: '6px 12px', borderRadius: '6px',
                                    border: '1px solid #e5e7eb', background: '#f3f4f6',
                                    color: '#9ca3af', fontSize: '0.75rem', fontWeight: '600',
                                    cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '4px'
                                } : {
                                    // Scenario B: Needed (Urgent Red)
                                    padding: '6px 12px', borderRadius: '6px',
                                    border: 'none', background: '#e11d48',
                                    color: 'white', fontSize: '0.75rem', fontWeight: '600',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                    boxShadow: '0 1px 2px rgba(225, 29, 72, 0.2)'
                                }}
                            >
                                {isFixing ? (
                                    <><span>In Progress</span> <span>⏳</span></>
                                ) : (
                                    <><span>⚡ Start Fix</span></>
                                )}
                            </button>
                        )}

                        {/* Rank Badge (Standard) */}
                        {!isLoss && (
                            <div style={{
                                padding: '4px 10px',
                                borderRadius: '6px',
                                background: bgColor,
                                color: borderColor === '#ef4444' ? '#b91c1c' : (borderColor === '#10b981' ? '#065f46' : '#3f6212'),
                                border: `1px solid ${borderColor}40`,
                                textAlign: 'center',
                                minWidth: '60px'
                            }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', opacity: 0.8 }}>Rank</div>
                                <div style={{ fontSize: '1.1rem', fontWeight: '800', lineHeight: '1' }}>
                                    {formatRankDisplay(item._bestRank)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // [NEW] Progress Notification Item (Visualization)


    // Colors
    const COLOR_WIN = "#7fc684";  // Global Pastel Green
    const COLOR_LOSS = "#e37275"; // Global Pastel Red
    const BG_WIN = "#F0FDF4";
    const BG_LOSS = "#FEF2F2";

    // --- LOGIC ---
    const handleFix = (battle) => {
        const id = battle.id;

        // Optimistic UI update immediately
        setFixingIds(prev => ({ ...prev, [id]: true }));
        console.log(`[SmartFeed] Moving '${battle.keyword}' to In Progress...`);

        // Perform API Call for persistence
        return apiService.makeRequest('/reputation/tasks', {
            method: 'POST',
            body: JSON.stringify({
                source_battle_id: battle.id,
                location_id: battle.profile_id, // Use profile ID for location logic
                // [FIX] Ensure we have a query. Fallback to title clean if needed.
                query: battle.keyword || (battle.title ? battle.title.split(' (')[0] : "Unknown Query"),
                priority: "normal",
                client_profile_id: battle.profile_id
            })
        }).then(() => {
            // Success
            setFixingIds(prev => ({ ...prev, [id]: true }));
        }).catch(err => {
            console.error("Fix failed", err);
            // Revert optimistic update on hard error?
            // Maybe keep it to not jar user, let refresh handle it.
            // setFixingIds(prev => { ...prev, [id]: false });
        });
    };

    /**
     * Extracts competitor names from a numbered list in AI response text.
     * Use Whitelist/Blacklist strategy to filter garbage.
     */
    const extractCompetitors = (battle) => {
        // [FIX] Prioritize initial_message (Search Results) over analysis
        const text = battle.initial_message || battle.message_content;
        if (!text) return [];

        // 1. Capture list items (numbered or bulleted)
        const regex = /(?:^|\n)(?:\d+\.|[-*])\s+(?:\*\*)?([^*\n]+)(?:\*\*)?/g;
        const matches = [...text.matchAll(regex)].map(m => m[1].trim());

        // 2. Define "Real Firm" indicators (Optional but good)
        // const FIRM_KEYWORDS = ["Law", "Legal", "Attorney", "Firm", "P.C.", "PC", "LLP", "LLC", "Esq", "Group", "Associates", "Office"];

        // 3. Define Blacklist (Trash removal)
        const BLACKLIST = [
            "Client Reviews", "Experience", "Factors", "Review", "Criteria", "Methodology", "Forums", "Directories",
            "Portals", "Websites", "Search", "Google", "Yelp", "Avvo",
            "FindLaw", "Justia", "Super Lawyers", "Experience", "Reputation",
            "Services", "Association", "Bar", "Search", "Google"
        ];

        // 4. Filtering Logic
        return matches.filter(name => {
            // const hasKeyword = FIRM_KEYWORDS.some(kw => name.includes(kw));
            const isClean = !BLACKLIST.some(bad => name.includes(bad));
            const isNotMorgan = !/Morgan Legal|MLG/i.test(name);
            return isClean && isNotMorgan && name.length > 3;
        }).slice(0, 3);
    };

    // Fake content if not present for simulation of extraction
    const getSimulationText = (battle) => {
        // [FIX] Prioritize initial_message (Search Results) over analysis
        let rawContent = battle.initial_message || battle.message_content;
        try {
            if (typeof rawContent === 'string' && (rawContent.startsWith('{') || rawContent.startsWith('['))) {
                const parsed = JSON.parse(rawContent);
                return parsed.ai_response || parsed.result || parsed.content || JSON.stringify(parsed, null, 2);
            }
        } catch (e) { }

        if (rawContent) return rawContent;
        return `Based on your request for ${battle.keyword} in ${battle.location}, I found these top options:\n\n1. ${battle.competitor || 'LegalZoom'} - Known for low cost.\n2. **Smith & Associates** - Full service firm.\n3. Frank T. Adams - Local specialist.\n\nMorgan Legal Group was not found in the initial search results.`;
    };

    // [NEW] Progress Mapping Logic (Deterministic)
    const getProgressStats = (battle) => {
        const status = (battle.active_task_status || battle.status || 'pending').toLowerCase();

        // [FIX] Explicit Color Codes for Inline Styles
        switch (status) {
            case 'pending':
            case 'queued':
                return { percent: 10, label: 'Queued in Matrix', colorCode: '#3b82f6' }; // Blue
            case 'active':
            case 'in_progress':
            case 'running':
                return { percent: 45, label: '⚡ Agent Working...', colorCode: '#f59e0b' }; // Amber
            case 'analyzing':
            case 'processing':
                return { percent: 70, label: '🔍 Analyzing Data...', colorCode: '#a855f7' }; // Purple
            case 'finalizing':
            case 'optimizing':
            case 'writing':
                return { percent: 90, label: '📝 Finalizing Report...', colorCode: '#10b981' }; // Emerald
            case 'completed':
            case 'success':
                return { percent: 100, label: 'Completed', colorCode: '#22c55e' }; // Green
            case 'failed':
            case 'error':
                return { percent: 100, label: 'Failed', colorCode: '#ef4444' }; // Red
            default:
                return { percent: 10, label: 'Initializing...', colorCode: '#9ca3af' }; // Gray
        }
    };

    const ProgressItem = ({ item }) => {
        // [FIX] Use Deterministic Stats
        const stats = getProgressStats(item);

        // [FIX] Title Logic - Prioritize Query
        let displayTitle = item.strategic_task?.query || item.query || item.keyword || item.title || "Unknown Task";

        // Clean "Auto: " prefix and timestamp if it leaked into title
        if (displayTitle && displayTitle.toString().toLowerCase().startsWith('auto:')) {
            // Remove "Auto:" and any trailing date in parens "(Dec 25...)"
            displayTitle = displayTitle.replace(/^auto:\s*/i, '').replace(/\s*\(.*?\)$/, '').trim();
        }

        // [FIX] Time Logic - Handle Future Dates & Timezones
        const formatTime = (date) => {
            if (!date) return 'Just now';
            const m = moment(date);
            if (!m.isValid()) return 'Just now';

            // If date is in future (e.g. UTC vs Local skew), show 'Just now'
            // [Adjusted] Allow 5 minute buffer for clock skew
            if (m.isAfter(moment().add(5, 'minutes'))) return 'Processing...';

            return m.fromNow();
        };

        return (
            <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>
                        {displayTitle} <span style={{ fontSize: '0.8em', fontWeight: '400', color: '#94a3b8' }}>({formatTime(item.created_at)})</span>
                    </div>
                    <div style={{ fontWeight: '700', color: stats.colorCode, fontSize: '0.8rem' }}>{stats.percent}%</div>
                </div>

                {/* Progress Bar Container */}
                <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                    <div
                        className={`h-full rounded-full transition-all duration-500 ease-out`}
                        style={{
                            width: `${stats.percent}%`,
                            backgroundColor: stats.colorCode // [FIX] Force Inline Color
                        }}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#64748b' }}>
                    <Loader2 size={12} className="spin" />
                    <span>{stats.label}</span>
                </div>
            </div>
        );
    };

    // --- MODAL COMPONENT ---
    const detailsBattle = viewDetailsBattle;
    const extractedCompetitors = useMemo(() => {
        if (!detailsBattle) return [];
        return extractCompetitors(detailsBattle);
    }, [detailsBattle]);

    // RENDER HELPERS
    // --- MODAL COMPONENT ---
    // [REFAC] Using external ContentWarfareModal now.
    // Logic moved to src/components/reputation/ContentWarfareModal.js



    if (!battles || battles.length === 0) {
        return (
            <div className="card" style={{ height: '100%', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                <Activity size={48} style={{ opacity: 0.5, marginBottom: '20px' }} />
                <h3>No Recent History</h3>
                <p>Battle data will appear here.</p>
            </div>
        );
    }

    return (
        <div className="card" id="smart-feed-card" style={{
            height: height || '500px',
            minHeight: '400px',
            padding: '0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            position: 'relative'
        }}>
            <style>{`
                .smart-feed-scroll::-webkit-scrollbar { width: 6px; }
                .smart-feed-scroll::-webkit-scrollbar-track { background: transparent; }
                .smart-feed-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
            `}</style>
            {/* Header with Filters */}
            <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                background: 'white',
                zIndex: 10
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>
                            Recent Battle Cards
                        </h3>
                    </div>
                </div>

                {/* TABS */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    {[
                        { id: 'all', label: 'All Actions ⚡' },
                        { id: 'win', label: 'Wins 🏆' },
                        { id: 'loss', label: 'Losses 🛡️' },
                        {
                            id: 'progress',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    In Progress ⏳
                                    {Object.keys(fixingIds).length > 0 && (
                                        <Loader2 size={12} className="spin" />
                                    )}
                                </span>
                            )
                        },
                        { id: 'trends', label: 'Trends 🚀' }
                    ].map(tab => {
                        const isActive = filter === tab.id;

                        // [NEW] Dynamic Active Colors
                        let activeBg = 'white';
                        let activeBorder = '#1e293b';
                        let activeColor = '#1e293b';

                        if (isActive) {
                            if (tab.id === 'win') {
                                activeBg = '#dcfce7'; activeBorder = '#16a34a'; activeColor = '#15803d';
                            } else if (tab.id === 'loss') {
                                activeBg = '#fef2f2'; activeBorder = '#dc2626'; activeColor = '#b91c1c';
                            } else if (tab.id === 'progress') {
                                activeBg = '#dbeafe'; activeBorder = '#2563eb'; activeColor = '#1d4ed8';
                            } else if (tab.id === 'trends') {
                                activeBg = '#faf5ff'; activeBorder = '#9333ea'; activeColor = '#7e22ce';
                            }
                        }

                        const activeStyle = {
                            background: activeBg,
                            border: `1px solid ${activeBorder}`,
                            color: activeColor,
                            fontWeight: '700',
                            borderRadius: '6px'
                        };
                        const inactiveStyle = {
                            background: '#f1f5f9', // Light Gray
                            border: '1px solid transparent', // Maintain layout size
                            color: '#64748b',
                            borderRadius: '6px'
                        };

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setFilter(tab.id)}
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    ...(isActive ? activeStyle : inactiveStyle)
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Scrollable Feed */}
            <div className="smart-feed-scroll" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '15px',
                background: '#F8FAFC'
            }}>
                {filteredBattles.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#cbd5e1' }}>No events found for this filter.</div>
                ) : (
                    <>
                        {/* Slice data for view */
                            filteredBattles.slice(0, visibleCount).map((battle, index) => {
                                if (filter === 'trends') return <TrendItem key={battle.id || index} item={battle} />;
                                if (filter === 'progress') return <ProgressItem key={battle.id || index} item={battle} />;
                                return (
                                    <BattleCard
                                        key={battle.id || index}
                                        battle={battle}
                                        isFixing={battle._isInProgress}
                                        uiStatus={battle._uiStatus}
                                        computedRank={battle._bestRank}
                                        defaultLocation={defaultLocation}
                                        onFix={() => { }} // Deprecated
                                        onResolve={handleResolve}
                                        onView={handleView}
                                    />
                                );
                            })}

                        {/* Load More Button */
                            visibleCount < filteredBattles.length && (
                                <button
                                    onClick={() => setVisibleCount(prev => prev + 50)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        marginTop: '15px',
                                        marginBottom: '10px',
                                        background: '#f1f5f9',
                                        color: '#64748b',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => { e.target.style.background = '#e2e8f0'; e.target.style.color = '#475569'; }}
                                    onMouseLeave={(e) => { e.target.style.background = '#f1f5f9'; e.target.style.color = '#64748b'; }}
                                >
                                    Load More ({filteredBattles.length - visibleCount} remaining)
                                </button>
                            )}
                    </>
                )}
            </div>

            <ContentWarfareModal
                isOpen={!!viewDetailsBattle}
                onClose={() => setViewDetailsBattle(null)}
                battle={detailsBattle}
                onQueueTask={handleQueueTask} // Reuse existing queuing logic
                initialMode={modalMode}
            />
        </div>
    );
};

export default SmartReputationFeed;
